import { useState, useEffect, FC } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { getEventFlags, FlagTrans, getStartEndFromTimeDate } from 'services/schedule'
import hasAccessToThis from 'services/utilFunctions/hasAccessToThis'

import { Alert, Spinner } from 'ui'
import { openModal } from 'store/actions/modals'

import { t } from 'translations'

type ShiftId = string
type UserFlags = MData.UserWithFlags['flags']['shiftFlags'][ShiftId]

const AssignedUserFlags: FC<{ event: MData.Event & { procedureId: string }; setValidFlags?: (validFlags?: string[]) => void }> = ({ event, setValidFlags }) => {
  const dispatch = useDispatch()
  const { users: usersRefresh } = useSelector((state: MStore.RootState) => state.refresh)
  const { enableEligibilityFix } = useSelector((state: MStore.RootState) => state.settings)
  const canManageUser = hasAccessToThis('userUpdateHiringInfo')
  const canModifyEntireShift = hasAccessToThis('scheduleAssignEmployee') && hasAccessToThis('scheduleModify')

  const [userFlags, setUserFlags] = useState<UserFlags | undefined>(undefined)
  const [numberOfHoursPerRange, setNumberOfHoursPerRange] = useState<MData.UserWithFlags['numberOfHoursPerRange'] | undefined>(undefined)
  const [loadingFlags, setLoadingFlags] = useState(true)

  const employeeId = event.employeeId

  let eventProjects = event.projects
  if (!Array.isArray(event.projects)) eventProjects = event.projects ? [event.projects] : []

  // Variables used to avoid refreshing user flags every time the comment is changed
  const [eventStart, eventEnd] = getStartEndFromTimeDate(event)
  const event_id = event._id
  const eventStartTimezone = event.startTimezone
  const eventEndTimezone = event.endTimezone
  const eventPositionId = event.positionId
  const eventTags = event.tags
  const eventRequiredAttributes = event.requiredAttributes
  const eventProject = eventProjects?.[0]
  const eventTeam = event.team
  const eventBreaks = event.breaks
  const eventProcedureId = event.procedureId

  useEffect(() => {
    const getEventFlagsUseEffect = async () => {
      setLoadingFlags(true)
      const { success, userFlags, numberOfHoursPerRange, validFlags } = await getEventFlags(
        {
          _id: event_id,
          employeeId: employeeId,
          start: eventStart ?? '',
          startTimezone: eventStartTimezone,
          end: eventEnd ?? '',
          endTimezone: eventEndTimezone,
          positionId: eventPositionId,
          procedureId: eventProcedureId,
          projects: eventProject ? [eventProject] : [],
          team: eventTeam,
          tags: eventTags,
          breaks: eventBreaks,
          requiredAttributes: eventRequiredAttributes
        },
        true
      )

      if (success) {
        if (setValidFlags) setValidFlags(validFlags)
        setUserFlags(userFlags)
        setNumberOfHoursPerRange(numberOfHoursPerRange)
        setLoadingFlags(false)
      }
    }

    if (eventPositionId && employeeId) getEventFlagsUseEffect()
  }, [
    event_id,
    eventStart,
    eventStartTimezone,
    eventEnd,
    eventEndTimezone,
    eventPositionId,
    eventProject,
    eventTeam,
    eventTags,
    eventBreaks,
    employeeId,
    setValidFlags,
    eventRequiredAttributes,
    usersRefresh,
    eventProcedureId
  ])

  if (!employeeId || !eventPositionId) return null

  if (loadingFlags) return <Spinner className="mr-2" size="lg" />

  // Handle case where flags are not defined
  if (typeof userFlags === 'undefined') return <Alert variant="warning">{t('manager.schedule.EDIT_MODAL.USER_FLAGS.COULD_NOT_GET_ISSUES')}</Alert>

  // Check if the user is eligible
  const { isEligible, reasons, ...rest } = userFlags

  const reasonsKeys = Object.keys(rest) as MData.UserFlags[]

  const missingAttributes = userFlags?.missingSkills || userFlags?.missingRequirements || userFlags?.hasRestrictions
  const canFixEligibilityProblems =
    userFlags?.missingRegion || userFlags?.missingPosition || missingAttributes || userFlags?.wrongTeam || userFlags?.wrongDepartment || userFlags?.wrongProject

  const canDisplayQuickEligibilityFixLink = !isEligible && enableEligibilityFix && canManageUser && canFixEligibilityProblems && canModifyEntireShift

  return (
    <>
      <Alert show={!!reasons.userMightBeOnHoliday} variant="info">
        {t('manager.schedule.EDIT_MODAL.USER_FLAGS.userMightBeOnHoliday')}
      </Alert>
      <Alert variant={isEligible ? 'success' : 'warning'}>
        <b>{t(`manager.schedule.EDIT_MODAL.USER_FLAGS.${isEligible ? 'USER_IS_ELIGIBLE' : 'USER_IS_NOT_ELIGIBLE'}`)}</b>
        {reasonsKeys.map(flag => {
          return <FlagTrans key={flag} flag={flag} event={event} numberOfHoursPerRange={numberOfHoursPerRange} userFlags={userFlags} />
        })}
        {canDisplayQuickEligibilityFixLink ? (
          <Alert.Action
            onClick={() => {
              dispatch(
                openModal('ScheduleFixUserEligibilityProblemsModal', {
                  event,
                  missingAttributes,
                  missingPosition: userFlags?.missingPosition,
                  missingRegion: userFlags?.missingRegion,
                  missingRegions: reasons.missingRegions,
                  userId: employeeId,
                  wrongDepartment: userFlags?.wrongDepartment,
                  wrongProject: userFlags?.wrongProject,
                  wrongTeam: userFlags?.wrongTeam
                })
              )
            }}
          >
            <i className={`<i fas fa-bolt fa-md mr-2`} />
            {!isEligible ? t('manager.schedule.EDIT_MODAL.USER_FLAGS.SOLVE_PROBLEMS') : null}
          </Alert.Action>
        ) : null}
      </Alert>
    </>
  )
}

export default AssignedUserFlags
